import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  Area,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
} from "recharts";
import { api } from "../api";
import {
  discountPct,
  formatDate,
  formatDateTime,
  formatMoney,
} from "../lib/format";
import { computeBadges, type Badge } from "../lib/badges";
import { getUserId } from "../lib/userId";
import type { PriceHistoryPoint } from "../types";

const RANGE_OPTIONS: Array<{ label: string; days: number }> = [
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "180d", days: 180 },
  { label: "1 año", days: 365 },
];

function scoreClass(score: number): string {
  if (score >= 80) return "badge-score-hi";
  if (score >= 60) return "badge-score-mid";
  return "badge-score-lo";
}

export function ProductDetail() {
  const { id = "" } = useParams();
  const [days, setDays] = useState(90);
  const [followHover, setFollowHover] = useState(false);
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const userId = getUserId();
  const queryClient = useQueryClient();

  const product = useQuery({
    queryKey: ["product", id],
    queryFn: () => api.product(id),
  });
  const history = useQuery({
    queryKey: ["history", id, days],
    queryFn: () => api.history(id, days),
  });
  const analysis = useQuery({
    queryKey: ["analysis", id],
    queryFn: () => api.analysis(id),
    retry: false,
  });
  const watchlist = useQuery({
    queryKey: ["watchlist", userId],
    queryFn: () => api.watchlist(userId),
    staleTime: 60_000,
  });
  const watchlistEntry =
    watchlist.data?.find((w) => w.product_id === id) ?? null;
  const isFollowing = watchlistEntry !== null;

  const follow = useMutation({
    mutationFn: () => api.addWatchlist({ userId, productId: id }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["watchlist", userId] }),
  });

  const unfollow = useMutation({
    mutationFn: (watchlistId: string) => api.removeWatchlist(watchlistId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["watchlist", userId] }),
  });

  const saveNotes = useMutation({
    mutationFn: (notes: string) => api.saveNotes(id, notes),
    onSuccess: (res) => {
      queryClient.setQueryData(["product", id], (old: typeof product.data) =>
        old ? { ...old, notes: res.notes } : old,
      );
      setNotesDraft(null);
    },
  });

  const chartData = useMemo(
    () => buildChartData(history.data ?? []),
    [history.data],
  );

  const badges = useMemo<Badge[]>(() => {
    if (!product.data) return [];
    return computeBadges({
      product: product.data,
      history: history.data ?? [],
      analysis: analysis.data ?? null,
    });
  }, [product.data, history.data, analysis.data]);

  if (product.isLoading) return <div className="loading">Cargando…</div>;
  if (product.isError)
    return (
      <div className="error">Error: {(product.error as Error).message}</div>
    );
  if (!product.data) return <div className="empty">No encontrado.</div>;

  const p = product.data;
  const pct = discountPct(p.current_price_cents, p.list_price_cents);

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Link to="/" style={{ color: "var(--text-dim)" }}>
          ← Volver
        </Link>
      </div>

      <div className="detail-grid" style={{ marginBottom: 24 }}>
        <div>
          {p.image_url ? (
            <div className="detail-img">
              <img src={p.image_url} alt={p.name} />
            </div>
          ) : (
            <div className="card-img-fallback" style={{ height: 240 }}>
              sin imagen
            </div>
          )}
        </div>
        <div>
          <h2 style={{ marginTop: 0 }}>{p.name}</h2>
          <div className="card-price-row" style={{ marginBottom: 12 }}>
            <span className="card-price" style={{ fontSize: 24 }}>
              {formatMoney(p.current_price_cents)}
            </span>
            {p.list_price_cents &&
              p.list_price_cents !== p.current_price_cents && (
                <span className="card-list" style={{ fontSize: 14 }}>
                  {formatMoney(p.list_price_cents)}
                </span>
              )}
            {pct != null && (
              <span className="badge badge-discount">-{pct}%</span>
            )}
          </div>
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <button
              onClick={() => {
                if (isFollowing) {
                  if (watchlistEntry) unfollow.mutate(watchlistEntry.id);
                } else {
                  follow.mutate();
                }
              }}
              disabled={follow.isPending || unfollow.isPending}
              onMouseEnter={() => setFollowHover(true)}
              onMouseLeave={() => setFollowHover(false)}
              style={{
                padding: "8px 20px",
                borderRadius: 8,
                border:
                  isFollowing && followHover
                    ? "1px solid #e74c3c"
                    : "1px solid transparent",
                cursor: "pointer",
                background:
                  isFollowing && followHover
                    ? "transparent"
                    : isFollowing
                      ? "var(--border)"
                      : "var(--accent)",
                color:
                  isFollowing && followHover
                    ? "#e74c3c"
                    : isFollowing
                      ? "var(--text)"
                      : "#0f1115",
                fontWeight: 600,
                fontSize: 14,
                minWidth: 140,
                transition:
                  "background-color 380ms ease, color 380ms ease, border-color 380ms ease",
              }}
              title={
                isFollowing
                  ? "Click para dejar de seguir"
                  : "Seguir este producto"
              }
            >
              {unfollow.isPending
                ? "Quitando…"
                : follow.isPending
                  ? "Guardando…"
                  : isFollowing
                    ? followHover
                      ? "Dejar de seguir"
                      : "Siguiendo"
                    : "Seguir"}
            </button>
          </div>

          <dl className="detail-meta">
            <dt>Tienda</dt>
            <dd>{p.store_id}</dd>
            {p.brand && (
              <>
                <dt>Marca</dt>
                <dd>{p.brand}</dd>
              </>
            )}
            {p.sku && (
              <>
                <dt>SKU</dt>
                <dd>{p.sku}</dd>
              </>
            )}
            <dt>Visto por última vez</dt>
            <dd>{formatDateTime(p.last_seen_at)}</dd>
            <dt>Link</dt>
            <dd>
              <a href={p.url} target="_blank" rel="noreferrer noopener">
                Ver en la tienda ↗
              </a>
            </dd>
          </dl>
        </div>

        <div className="chart-wrap detail-notes" style={{ marginBottom: 0 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>Notas</h3>
          {notesDraft === null && (
            <button
              onClick={() => setNotesDraft(p.notes ?? "")}
              style={{
                background: "var(--bg)",
                color: "var(--text-dim)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              {p.notes ? "Editar" : "Agregar nota"}
            </button>
          )}
        </div>

        {notesDraft === null ? (
          p.notes ? (
            <p style={{ whiteSpace: "pre-wrap", margin: 0, lineHeight: 1.5 }}>
              {p.notes}
            </p>
          ) : (
            <div className="empty" style={{ padding: 0 }}>
              Sin notas todavía.
            </div>
          )
        ) : (
          <div>
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="Escribe tus apuntes sobre este producto…"
              rows={4}
              autoFocus
              style={{
                width: "100%",
                boxSizing: "border-box",
                background: "var(--bg)",
                color: "var(--text)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: 10,
                fontSize: 14,
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button
                onClick={() => saveNotes.mutate(notesDraft)}
                disabled={saveNotes.isPending}
                style={{
                  background: "var(--accent)",
                  color: "#0f1115",
                  border: "1px solid transparent",
                  borderRadius: 6,
                  padding: "6px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {saveNotes.isPending ? "Guardando…" : "Guardar"}
              </button>
              <button
                onClick={() => setNotesDraft(null)}
                disabled={saveNotes.isPending}
                style={{
                  background: "var(--bg)",
                  color: "var(--text-dim)",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  padding: "6px 16px",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Cancelar
              </button>
            </div>
            {saveNotes.isError && (
              <div className="error" style={{ marginTop: 8 }}>
                Error al guardar la nota.
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {badges.length > 0 && (
        <div className="badges-row">
          {badges.map((b, i) => (
            <span
              key={`${b.label}-${i}`}
              className={`product-badge product-badge-${b.tone}`}
              title={b.title}
            >
              {b.label}
            </span>
          ))}
        </div>
      )}

      {analysis.data && (
        <div className="analysis">
          <div
            className={`analysis-score ${scoreClass(analysis.data.dealScore)}`}
          >
            {analysis.data.dealScore}
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Deal score</div>
            <div className="analysis-reason">{analysis.data.reason}</div>
          </div>
          <div className="analysis-stats">
            <Stat
              label="Mínimo 90d"
              value={formatMoney(analysis.data.min90dCents)}
            />
            <Stat
              label="P20 90d"
              value={formatMoney(analysis.data.p20_90dCents)}
            />
            <Stat
              label="Mediana 180d"
              value={formatMoney(analysis.data.median180dCents)}
            />
            <Stat
              label="Días ≤ precio actual (1 año)"
              value={String(analysis.data.daysBelowCurrent)}
            />
          </div>
        </div>
      )}
      {analysis.isError && (
        <div className="empty">
          Sin suficiente historial para análisis (mínimo 7 puntos).
        </div>
      )}

      <div className="chart-wrap">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3>Historial de precio</h3>
          <div style={{ display: "flex", gap: 4 }}>
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.days}
                onClick={() => setDays(opt.days)}
                style={{
                  background: days === opt.days ? "var(--accent)" : "var(--bg)",
                  color: days === opt.days ? "#0f1115" : "var(--text-dim)",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {history.isLoading && (
          <div className="loading">Cargando histórico…</div>
        )}
        {history.data && history.data.length === 0 && (
          <div className="empty">Sin puntos de precio en este rango.</div>
        )}

        {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart
              data={chartData}
              margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
            >
              <defs>
                <linearGradient id="priceFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4fb3ff" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#4fb3ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="ts"
                type="number"
                scale="time"
                domain={["dataMin", "dataMax"]}
                tickFormatter={(ts) => formatDate(new Date(ts).toISOString())}
                stroke="#8a92a3"
                fontSize={11}
                minTickGap={40}
              />
              <YAxis
                stroke="#8a92a3"
                fontSize={11}
                tickFormatter={(v) => `S/ ${(v / 100).toFixed(0)}`}
                domain={["auto", "auto"]}
              />
              <Tooltip
                contentStyle={{
                  background: "#181b22",
                  border: "1px solid #262a33",
                  borderRadius: 6,
                }}
                labelFormatter={(ts) =>
                  formatDateTime(new Date(ts).toISOString())
                }
                formatter={(value: number, name: string) => [
                  formatMoney(value),
                  name,
                ]}
              />
              <Area
                type="monotone"
                dataKey="current"
                name="Precio"
                stroke="#4fb3ff"
                strokeWidth={2}
                fill="url(#priceFill)"
                isAnimationActive={false}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="list"
                name="Precio tachado"
                stroke="#8a92a3"
                strokeDasharray="4 4"
                strokeWidth={1}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
              {analysis.data && (
                <ReferenceLine
                  y={analysis.data.median180dCents}
                  stroke="#ffb84d"
                  strokeDasharray="2 4"
                  label={{
                    value: "mediana 180d",
                    position: "insideBottomRight",
                    fill: "#ffb84d",
                    fontSize: 10,
                  }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="analysis-stat">
      <span className="analysis-stat-label">{label}</span>
      <span className="analysis-stat-value">{value}</span>
    </div>
  );
}

function buildChartData(history: PriceHistoryPoint[]) {
  return history.map((h) => ({
    ts: new Date(h.captured_at).getTime(),
    current: h.current_price_cents,
    list: h.list_price_cents,
  }));
}
